import React, { useContext, useCallback, useRef, type FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
} from '@atlaskit/side-nav-items/flyout-menu-item';

import { useRouteDataRef, useRouteUrl } from '@confluence/route-manager/entry-points/RouteState.ts';
import { LoadableLazy } from '@confluence/loadable';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';
import {
	GO_TO_STARRED_SHORTCUT,
	UNIVERSAL_GO_TO_STARRED_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { APP_NAV_PAGES_DROPDOWN_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { HOME } from '@confluence/named-routes/entry-points/routes.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useFlyout } from '@confluence/flyout-store';

import { KeyboardShortcutWrapper } from '../../PlatformNavigation/KeyboardShortcutWrapper.tsx';
import { i18n } from '../globalNavigationTranslations.ts';
import { StarIconComponent } from '../GlobalNavigationIcons.tsx';
import { preloadStarredContentMenu } from '../../ContentMenu/preloadStarredContentMenu.ts';
import { ContentMenuLoadingState } from '../../ContentMenu/ContentMenuLoadingStates.tsx';

import { useGlobalItemVisibility } from './useGlobalItemVisibility.ts';
import type { GlobalItemProps } from './globalItemProps.ts';

export const StarredContentMenuLoader = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-StarredContentMenu" */
				'../../ContentMenu/StarredContentMenu.tsx'
			)
		).StarredContentMenu,
	loading: () => <ContentMenuLoadingState />,
});

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ '../analyticsCallbacks.ts'))
			.fireStarredClickedAnalytics,
);

const MENU_ID = 'starred';

export const StarredItem: FC<GlobalItemProps> = ({ isHidden, peekingId, setPeekingId }) => {
	const { isFlyoutOpen, openFlyout, closeFlyout } = useFlyout(MENU_ID);

	const routeDataRef = useRouteDataRef();
	const hasBeenOpenedBefore = useRef(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, routeDataRef);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();

	const abortExperienceTracker = useCallback(() => {
		experienceTracker.abort({
			name: APP_NAV_PAGES_DROPDOWN_EXPERIENCE,
			reason: 'Primary dropdown menu: closed by user',
			attributes: {
				navVersion: '4',
				dropdownType: 'starred',
			},
		});
	}, [experienceTracker]);

	const startExperienceTracker = useCallback(() => {
		experienceTracker.start({
			name: APP_NAV_PAGES_DROPDOWN_EXPERIENCE,
			timeout: ExperienceTimeout.NAVIGATION_LOAD,
			attributes: {
				navVersion: '4',
				dropdownType: 'starred',
			},
		});
	}, [experienceTracker]);

	const onOpenChange = useCallback(
		(newIsOpen: boolean) => {
			if (!newIsOpen) {
				abortExperienceTracker();
				// onOpenChange is called whenever FlyoutMenuItem component mounts, but we
				// only want to fire analytics event on user interaction. We check here if the
				// flyout has been opened before to ignore onOpenChange calls before user interaction
				if (hasBeenOpenedBefore.current) {
					void fireClickAnalytics(false);
				}
				isFlyoutOpen && closeFlyout();
			} else {
				openFlyout();
				hasBeenOpenedBefore.current = true;
			}
		},
		[abortExperienceTracker, fireClickAnalytics, isFlyoutOpen, closeFlyout, openFlyout],
	);

	const onShortcutTriggered = useCallback(() => {
		if (!isFlyoutOpen) {
			void fireClickAnalytics(true, true, isHidden);
			startExperienceTracker();
			openFlyout();
		}
	}, [fireClickAnalytics, isFlyoutOpen, startExperienceTracker, isHidden, openFlyout]);

	const onClick = useCallback(() => {
		if (!isFlyoutOpen) {
			openFlyout();
			startExperienceTracker();
			void fireClickAnalytics(true); //if we clicked on it we know it's not hidden or fired via keyboard shortcut
		} else {
			closeFlyout();
		}
	}, [fireClickAnalytics, isFlyoutOpen, startExperienceTracker, openFlyout, closeFlyout]);

	const onHover = useCallback(() => {
		if (!isFlyoutOpen) {
			void StarredContentMenuLoader.preload();
			void preloadStarredContentMenu();
		}
	}, [isFlyoutOpen]);

	const isSelected = useRouteUrl(isOnStarredRoute);
	const shouldHide = useGlobalItemVisibility(
		MENU_ID,
		isSelected || isFlyoutOpen,
		isHidden,
		peekingId,
		setPeekingId,
	);

	if (shouldHide) {
		return null;
	}

	return (
		<KeyboardShortcutWrapper
			deprecatedKeyboardShortcut={
				!(isLiveEditMode || isOnEditRoute) ? GO_TO_STARRED_SHORTCUT : undefined
			}
			keyboardShortcut={UNIVERSAL_GO_TO_STARRED_SHORTCUT}
			isAppNavigationShortcut
			onShortcutTriggered={onShortcutTriggered}
		>
			<div onMouseEnter={onHover} onFocus={onHover}>
				<FlyoutMenuItem onOpenChange={onOpenChange} isOpen={isFlyoutOpen}>
					<FlyoutMenuItemTrigger
						elemBefore={StarIconComponent}
						onClick={onClick}
						isSelected={isSelected}
					>
						<FormattedMessage {...i18n.starred} />
					</FlyoutMenuItemTrigger>
					<FlyoutMenuItemContent onClose={() => isFlyoutOpen && closeFlyout()} autoFocus={false}>
						<StarredContentMenuLoader />
					</FlyoutMenuItemContent>
				</FlyoutMenuItem>
			</div>
		</KeyboardShortcutWrapper>
	);
};

const isOnStarredRoute = {
	selector: (routeUrl: string | undefined) => routeUrl === HOME.toUrl({ view: 'starred' }),
};
